// ============================================================
// 루미큐브 Zustand 클라 store  (SEQ-10214 S5c)
//   TTR ticket.ts 패턴 100% 정합 (proxy-subscribe 패턴)
//   정보 비대칭 강제:
//     - hand (본인 face): Caller scope broadcast에서만 세팅
//     - handSizes (타인 장수): Group broadcast HandSizeUpdated 에서만 세팅 (face 절대 포함 X)
//   TD api_contracts.hub_broadcasts 15종 apply* 메서드 1:1 등록
// ============================================================

import {
  RmkTile,
  RmkSetInfo,
  RmkScoreEntry,
  RmkFinalScoreEntry,
  RmkBoardTileInfo,
  RmkGameStartedPayload,
  RmkHandDealtPayload,
  RmkTileDrawnPayload,
  RmkBoardUpdatedPayload,
  RmkBoardSnapshotPayload,
  RmkHandSizeUpdatedPayload,
  RmkHandStateUpdatedPayload,
  RmkTurnEndedPayload,
  RmkTurnChangedPayload,
  RmkJokerRetrievedPayload,
  RmkInitialMeldCompletedPayload,
  RmkRoundEndedPayload,
  RmkGameEndedPayload,
  RmkPlayerConnectionChangedPayload,
  RmkGameSnapshotPayload,
  RmkWorkingStateUpdatedPayload,
} from '../hub/rummikubHub'

// ── re-export for convenience ─────────────────────────────
export type {
  RmkTile,
  RmkSetInfo,
  RmkScoreEntry,
  RmkFinalScoreEntry,
}

// ── state 타입 ────────────────────────────────────────────

export interface RmkGame {
  matchId:             number
  roundMode:           string  // SINGLE|CUMULATIVE
  totalRounds:         number
  currentRoundNo:      number
  gamePhase:           string  // SETUP|PLAYING|SCORING|ENDED
  turnNo:              number
  currentTurnUserCode: number | null
  turnDeadlineTs:      number  // epoch ms
  drawPileRemaining:   number
}

export interface RmkPlayer {
  userCode:          number
  nickname:          string
  seatIndex:         number
  turnOrder:         number
  firstMeldCompleted: boolean
  connectionState:   string  // CONNECTED|DISCONNECTED|PERMANENT_DISCONNECT
  cumulativeScore:   number
  round1stCount:     number
}

// 보드 위 세트: tileIds 외에 tile 상세를 boardTiles Map으로 별도 관리
export interface RmkBoardSet {
  setId:   number
  setType: string  // RUN|GROUP
  tileIds: number[]
}

// 타일 상세 (tileId → face — boardTiles에 캐시)
export interface RmkTileDetail {
  tileId:  number
  color:   string
  number:  number
  isJoker: boolean
}

// 라운드 점수
export interface RmkRoundScore {
  roundNo:        number
  winnerUserCode: number
  scores:         RmkScoreEntry[]
}

// 최종 점수
export interface RmkFinalScore {
  winnerUserCode: number
  finalScores:    RmkFinalScoreEntry[]
}

// 턴 종료 결과 (토스트/패널 표시용 단기 상태)
export interface RmkLastTurnResult {
  userCode:             number
  result:               string
  reason:               string
  meldValue?:           number
  isFirstMeldCompleted?: boolean
}

// ── store state ───────────────────────────────────────────

interface RmmikubState {
  myUserCode:        number | null
  game:              RmkGame | null
  players:           RmkPlayer[]
  // 본인 손패 (Caller scope — face 포함, 타인 접근 절대 금지)
  hand:              RmkTile[]
  // 보드 세트 목록 (BOARD_CONFIRMED 타일만 — Group broadcast 정합)
  boardSets:         RmkBoardSet[]
  // tileId → 상세 (보드 위 타일 렌더용 캐시)
  boardTiles:        Map<number, RmkTileDetail>
  // 타인 손패 장수 (face 없음, 수량만)
  handSizes:         Map<number, number>
  // firstMeld 완료 여부 (본인 포함)
  firstMeldStatus:   Map<number, boolean>
  // 누적 점수 (CUMULATIVE 모드)
  cumulativeScores:  Map<number, number>
  // 라운드 점수 이력
  roundScores:       RmkRoundScore[]
  // 최종 점수 (GameEnded)
  finalScore:        RmkFinalScore | null
  // 마지막 턴 결과 (TurnEnded 단기 표시)
  lastTurnResult:    RmkLastTurnResult | null
  // B-011 fix: 본인 BOARD_WORKING 시뮬레이션 세트 (Caller scope — WorkingStateUpdated)
  // boardSets(CONFIRMED)와 별도 관리. Undo 후 서버 권위로 덮어씀
  workingSets:       RmkBoardSet[]
}

// ── proxy-subscribe 패턴 (TTR ticket.ts 완전 정합) ──────────

let _state: RmmikubState = {
  myUserCode:       null,
  game:             null,
  players:          [],
  hand:             [],
  boardSets:        [],
  boardTiles:       new Map(),
  handSizes:        new Map(),
  firstMeldStatus:  new Map(),
  cumulativeScores: new Map(),
  roundScores:      [],
  finalScore:       null,
  lastTurnResult:   null,
  workingSets:      [],
}

const _listeners: Set<() => void> = new Set()

function notify() {
  _listeners.forEach(fn => { try { fn() } catch { /* 무시 */ } })
}

function setState(partial: Partial<RmmikubState>) {
  _state = { ..._state, ...partial }
  notify()
}

// ── 외부 subscribe ────────────────────────────────────────
export function subscribeRummikub(fn: () => void): () => void {
  _listeners.add(fn)
  return () => { _listeners.delete(fn) }
}

// ════════════════════════════════════════════════════════
// store public API
// ════════════════════════════════════════════════════════

export const rummikubStore = {
  // ── getter ─────────────────────────────────────────────
  getGame:             (): RmkGame | null   => _state.game,
  getPlayers:          (): RmkPlayer[]      => _state.players,
  getMyUserCode:       (): number | null    => _state.myUserCode,
  // 본인 손패 (Caller scope 전용 — 외부 노출 제한 의도)
  getMyHand:           (): RmkTile[]        => _state.hand,
  getBoardSets:        (): RmkBoardSet[]    => _state.boardSets,
  getBoardTiles:       (): Map<number, RmkTileDetail> => _state.boardTiles,
  // 타인 손패 장수 (face 없음)
  getHandSizes:        (): Map<number, number>         => _state.handSizes,
  getFirstMeldStatus:  (): Map<number, boolean>        => _state.firstMeldStatus,
  getCumulativeScores: (): Map<number, number>         => _state.cumulativeScores,
  getRoundScores:      (): RmkRoundScore[]             => _state.roundScores,
  getFinalScore:       (): RmkFinalScore | null        => _state.finalScore,
  getLastTurnResult:   (): RmkLastTurnResult | null    => _state.lastTurnResult,
  // B-011 fix: 본인 BOARD_WORKING 세트 (Caller scope — Undo 후 시뮬레이션 상태)
  getWorkingSets:      (): RmkBoardSet[]               => _state.workingSets,

  // ── setter ─────────────────────────────────────────────
  setMyUserCode(userCode: number): void {
    setState({ myUserCode: userCode })
  },

  clearGame(): void {
    setState({
      game:             null,
      players:          [],
      hand:             [],
      boardSets:        [],
      boardTiles:       new Map(),
      handSizes:        new Map(),
      firstMeldStatus:  new Map(),
      cumulativeScores: new Map(),
      roundScores:      [],
      finalScore:       null,
      lastTurnResult:   null,
      workingSets:      [],
    })
  },

  clearLastTurnResult(): void {
    setState({ lastTurnResult: null })
  },

  // ── broadcast apply* ────────────────────────────────────

  // Group: GameStarted
  applyGameStarted(p: RmkGameStartedPayload): void {
    const prev = _state.game
    setState({
      game: {
        matchId:             p.matchId,
        roundMode:           p.roundMode,
        totalRounds:         p.totalRounds,
        currentRoundNo:      1,
        gamePhase:           'PLAYING',
        turnNo:              1,
        currentTurnUserCode: p.currentTurnUserCode,
        turnDeadlineTs:      p.turnDeadlineTs,
        drawPileRemaining:   p.drawPileRemaining,
      },
      // turnOrder 배열로 players 순서 갱신 (seatIndex는 이미 로드됐다고 가정, turnOrder만 갱신)
      players: _state.players.map(pl => {
        const orderIdx = p.turnOrder.indexOf(pl.userCode)
        return orderIdx >= 0 ? { ...pl, turnOrder: orderIdx } : pl
      }),
    })
    // 본인 hand는 HandDealt(Caller)에서 수신
    void prev
  },

  // Caller: HandDealt (정보 비대칭 — 본인 face)
  applyHandDealt(p: RmkHandDealtPayload): void {
    setState({ hand: p.hand })
  },

  // Caller: TileDrawn (정보 비대칭 — 본인 face + drawPileRemaining)
  applyTileDrawn(p: RmkTileDrawnPayload): void {
    const newTile: RmkTile = {
      tileId:  p.tileId,
      color:   p.color,
      number:  p.number,
      isJoker: p.isJoker,
    }
    const updatedHand = [..._state.hand, newTile]
    const game = _state.game
    setState({
      hand: updatedHand,
      game: game ? { ...game, drawPileRemaining: p.drawPileRemaining } : game,
    })
  },

  // Group: BoardUpdated
  applyBoardUpdated(p: RmkBoardUpdatedPayload): void {
    // face 필드가 있으면 boardTiles Map에 즉시 캐시 (PLACE/MOVE 경우 서버가 face 포함)
    // 보드 세트 구조(boardSets) 자체는 BoardSnapshot full 갱신에 위임 — 여기서는 face만 반영
    if (p.tileId && p.tileColor !== undefined && p.tileNumber !== undefined && p.isJoker !== undefined) {
      const newBoardTiles = new Map(_state.boardTiles)
      newBoardTiles.set(p.tileId, {
        tileId:  p.tileId,
        color:   p.tileColor,
        number:  p.tileNumber,
        isJoker: p.isJoker,
      })
      setState({ boardTiles: newBoardTiles })
    }
  },

  // Group: BoardSnapshot (EndTurn 후 확정 보드 전체 상태)
  applyBoardSnapshot(p: RmkBoardSnapshotPayload): void {
    const sets: RmkBoardSet[] = p.sets.map(s => ({
      setId:   s.setId,
      setType: s.setType,
      tileIds: s.tileIds,
    }))
    // boardTiles Map 동시 갱신 (face 정보 포함)
    const newBoardTiles = new Map<number, RmkTileDetail>()
    if (p.boardTiles) {
      p.boardTiles.forEach((t: RmkBoardTileInfo) => {
        newBoardTiles.set(t.tileId, {
          tileId:  t.tileId,
          color:   t.color,
          number:  t.number,
          isJoker: t.isJoker,
        })
      })
    }
    setState({ boardSets: sets, boardTiles: newBoardTiles })
  },

  // Group: HandSizeUpdated (타인 손패 장수 — face 절대 포함 금지)
  applyHandSizeUpdated(p: RmkHandSizeUpdatedPayload): void {
    const newSizes = new Map(_state.handSizes)
    newSizes.set(p.userCode, p.handSize)
    const game = _state.game
    setState({
      handSizes: newSizes,
      game: game ? { ...game, drawPileRemaining: p.drawPileRemaining } : game,
    })
  },

  // Caller: HandStateUpdated (정보 비대칭 — 본인 face 갱신)
  applyHandStateUpdated(p: RmkHandStateUpdatedPayload): void {
    setState({ hand: p.hand })
  },

  // Group: TurnEnded
  applyTurnEnded(p: RmkTurnEndedPayload): void {
    setState({
      lastTurnResult: {
        userCode:             p.userCode,
        result:               p.result,
        reason:               p.reason,
        meldValue:            p.meldValue,
        isFirstMeldCompleted: p.isFirstMeldCompleted,
      },
    })
  },

  // Group: TurnChanged
  applyTurnChanged(p: RmkTurnChangedPayload): void {
    const game = _state.game
    if (!game) return
    setState({
      game: {
        ...game,
        currentTurnUserCode: p.nextUserCode,
        turnDeadlineTs:      p.turnDeadlineTs,
        turnNo:              p.turnNo,
      },
    })
  },

  // Group: JokerRetrieved
  applyJokerRetrieved(p: RmkJokerRetrievedPayload): void {
    // 보드는 다음 BoardSnapshot에서 갱신 — 여기서는 로그 용도
    void p
  },

  // Group: InitialMeldCompleted
  applyInitialMeldCompleted(p: RmkInitialMeldCompletedPayload): void {
    const newStatus = new Map(_state.firstMeldStatus)
    newStatus.set(p.userCode, true)
    setState({ firstMeldStatus: newStatus })
  },

  // Group: RoundEnded
  applyRoundEnded(p: RmkRoundEndedPayload): void {
    const newRoundScores = [..._state.roundScores, {
      roundNo:        p.roundNo,
      winnerUserCode: p.winnerUserCode,
      scores:         p.scores,
    }]
    // 누적 점수 갱신 (cumulativeAfter 기준)
    const newCumulative = new Map(_state.cumulativeScores)
    p.scores.forEach(s => { newCumulative.set(s.userCode, s.cumulativeAfter) })
    const game = _state.game
    setState({
      roundScores:      newRoundScores,
      cumulativeScores: newCumulative,
      game: game ? { ...game, gamePhase: 'SCORING' } : game,
    })
  },

  // Group: GameEnded
  applyGameEnded(p: RmkGameEndedPayload): void {
    const game = _state.game
    setState({
      finalScore: {
        winnerUserCode: p.winnerUserCode,
        finalScores:    p.finalScores,
      },
      game: game ? { ...game, gamePhase: 'ENDED' } : game,
    })
  },

  // Group: PlayerConnectionChanged
  applyPlayerConnectionChanged(p: RmkPlayerConnectionChangedPayload): void {
    const players = _state.players.map(pl =>
      pl.userCode === p.userCode
        ? { ...pl, connectionState: p.state }
        : pl,
    )
    setState({ players })
  },

  // Caller: GameSnapshot (재연결 — 본인 손패 포함 전체 상태)
  // SEQ=10219 catch: 서버 sp_RummikubGameSnapshot 7 resultset 구조 (matchHeader·players·boardSets·
  //   boardTiles·myHand·otherHandSizes·roundScoresIfAny) = REST PID 22001과 동일 페이로드.
  //   applyGameState로 위임하여 통합 hydrate (RmkGameSnapshotPayload 인터페이스는 폐기 영역, payload는 raw)
  applyGameSnapshot(p: unknown): void {
    rummikubStore.applyGameState(p as Record<string, unknown>)
  },

  // Caller: WorkingStateUpdated — B-011 fix
  // 서버 RummikubHub.cs L620: Clients.Caller.SendAsync("WorkingStateUpdated", { matchId, workingSets })
  // workingSets = GetWorkingStateSnapshot() 반환값 = { setId, setType, tileIds[] } 배열 (RmkSetInfo 동형)
  // Undo 후 본인 BOARD_WORKING 시뮬레이션 상태를 클라 store에 반영
  // boardSets(BOARD_CONFIRMED)와 완전 분리 — 정보 비대칭 강제
  applyWorkingStateUpdated(p: RmkWorkingStateUpdatedPayload): void {
    const workingSets: RmkBoardSet[] = p.workingSets.map(s => ({
      setId:   s.setId,
      setType: s.setType,
      tileIds: s.tileIds,
    }))
    setState({ workingSets })
  },

  // ── REST PID 22001 응답 hydrate (초기 로드용) ──────────────
  applyGameState(payload: Record<string, unknown>): void {
    // matchHeader (rs1) — 서버 rsList[0]은 List<Dictionary> = 1행 배열. SEQ=10221 첫 원소 추출
    const mhRaw = payload.matchHeader ?? payload.MatchHeader
    const mh: Record<string, unknown> | undefined = Array.isArray(mhRaw)
      ? (mhRaw[0] as Record<string, unknown> | undefined)
      : (mhRaw as Record<string, unknown> | undefined)
    if (mh) {
      // SEQ=10220 catch 1: SP rs1 출력 별칭은 snake_case (match_id, game_phase 등) — snake_case 매핑 추가
      const game: RmkGame = {
        matchId:             (mh.matchId ?? mh.MatchId ?? mh.match_id ?? 0) as number,
        roundMode:           (mh.roundMode ?? mh.RoundMode ?? mh.round_mode ?? 'SINGLE') as string,
        totalRounds:         (mh.totalRounds ?? mh.TotalRounds ?? mh.total_rounds ?? 1) as number,
        currentRoundNo:      (mh.currentRoundNo ?? mh.CurrentRoundNo ?? mh.current_round_no ?? 1) as number,
        gamePhase:           (mh.gamePhase ?? mh.GamePhase ?? mh.game_phase ?? 'SETUP') as string,
        turnNo:              (mh.turnNo ?? mh.TurnNo ?? mh.turn_no ?? 0) as number,
        currentTurnUserCode: (mh.currentTurnUserCode ?? mh.CurrentTurnUserCode ?? mh.current_turn_user_code ?? null) as number | null,
        turnDeadlineTs:      (mh.turnDeadlineTs ?? mh.TurnDeadlineTs ?? mh.turn_deadline_ts ?? 0) as number,
        drawPileRemaining:   (mh.drawPileRemaining ?? mh.DrawPileRemaining ?? mh.draw_pile_remaining ?? 0) as number,
      }
      setState({ game })
    }

    // players (rs2)
    const rawPlayers = (payload.players ?? payload.Players) as Array<Record<string, unknown>> | undefined
    if (rawPlayers) {
      // SEQ=10220 catch 1: SP rs2 출력 별칭은 snake_case (user_code, seat_index, turn_order 등) — snake_case 매핑 추가
      const players: RmkPlayer[] = rawPlayers.map(p => ({
        userCode:           (p.userCode ?? p.UserCode ?? p.user_code ?? 0) as number,
        nickname:           (p.nickname ?? p.Nickname ?? '') as string,
        seatIndex:          (p.seatIndex ?? p.SeatIndex ?? p.seat_index ?? 0) as number,
        turnOrder:          (p.turnOrder ?? p.TurnOrder ?? p.turn_order ?? 0) as number,
        firstMeldCompleted: Boolean(p.firstMeldCompleted ?? p.FirstMeldCompleted ?? p.first_meld_completed ?? 0),
        connectionState:    (p.connectionState ?? p.ConnectionState ?? p.connection_state ?? 'CONNECTED') as string,
        cumulativeScore:    (p.cumulativeScore ?? p.CumulativeScore ?? p.cumulative_score ?? 0) as number,
        round1stCount:      (p.round1stCount ?? p.Round1stCount ?? p.round_1st_count ?? 0) as number,
      }))
      setState({ players })

      // firstMeldStatus 동기
      const firstMeldStatus = new Map<number, boolean>()
      players.forEach(p => firstMeldStatus.set(p.userCode, p.firstMeldCompleted))
      setState({ firstMeldStatus })

      // cumulativeScores 동기
      const cumulativeScores = new Map<number, number>()
      players.forEach(p => cumulativeScores.set(p.userCode, p.cumulativeScore))
      setState({ cumulativeScores })
    }

    // boardSets (rs3) + boardTiles (rs4)
    const rawSets = (payload.boardSets ?? payload.BoardSets) as Array<Record<string, unknown>> | undefined
    const rawTiles = (payload.boardTiles ?? payload.BoardTiles) as Array<Record<string, unknown>> | undefined
    if (rawSets) {
      // SEQ=10220 catch 1: SP rs3 출력 별칭은 snake_case (set_id, set_type) — snake_case 매핑 추가
      const boardSets: RmkBoardSet[] = rawSets.map(s => ({
        setId:   (s.setId ?? s.SetId ?? s.set_id ?? 0) as number,
        setType: (s.setType ?? s.SetType ?? s.set_type ?? 'RUN') as string,
        tileIds: [],  // boardTiles에서 SET_ID 기준으로 채움
      }))
      const setMap = new Map<number, RmkBoardSet>()
      boardSets.forEach(s => setMap.set(s.setId, s))

      const boardTiles = new Map<number, RmkTileDetail>()
      if (rawTiles) {
        rawTiles.forEach(t => {
          // SEQ=10220 catch 1: SP rs4 출력 별칭은 snake_case (tile_id, tile_color, tile_number, is_joker) — snake_case 매핑 추가
          const tileId = (t.tileId ?? t.TileId ?? t.tile_id ?? 0) as number
          const setId  = (t.setId ?? t.SetId ?? t.set_id ?? 0) as number
          boardTiles.set(tileId, {
            tileId,
            color:   (t.tileColor ?? t.TileColor ?? t.tile_color ?? '') as string,
            number:  (t.tileNumber ?? t.TileNumber ?? t.tile_number ?? 0) as number,
            isJoker: Boolean(t.isJoker ?? t.IsJoker ?? t.is_joker ?? 0),
          })
          const bs = setMap.get(setId)
          if (bs) bs.tileIds.push(tileId)
        })
      }
      setState({ boardSets, boardTiles })
    }

    // myHand (rs5) — Caller scope, 본인만
    const rawHand = (payload.myHand ?? payload.MyHand) as Array<Record<string, unknown>> | undefined
    if (rawHand) {
      // SEQ=10220 catch 1: SP rs5 출력 별칭은 snake_case (tile_id, tile_color, tile_number, is_joker) — snake_case 매핑 추가
      const hand: RmkTile[] = rawHand.map(t => ({
        tileId:  (t.tileId ?? t.TileId ?? t.tile_id ?? 0) as number,
        color:   (t.tileColor ?? t.TileColor ?? t.tile_color ?? t.color ?? t.Color ?? '') as string,
        number:  (t.tileNumber ?? t.TileNumber ?? t.tile_number ?? t.number ?? t.Number ?? 0) as number,
        isJoker: Boolean(t.isJoker ?? t.IsJoker ?? t.is_joker ?? 0),
      }))
      setState({ hand })
    }

    // otherHandSizes (rs6) — face 없음, 수량만
    const rawHandSizes = (payload.otherHandSizes ?? payload.OtherHandSizes) as Array<Record<string, unknown>> | undefined
    if (rawHandSizes) {
      // SEQ=10220 catch 1: SP rs6 출력 별칭은 snake_case (user_code, hand_size) — snake_case 매핑 추가
      const handSizes = new Map<number, number>()
      rawHandSizes.forEach(hs => {
        handSizes.set(
          (hs.userCode ?? hs.UserCode ?? hs.user_code ?? 0) as number,
          (hs.handSize ?? hs.HandSize ?? hs.hand_size ?? 0) as number,
        )
      })
      setState({ handSizes })
    }

    // roundScores (rs7) — CUMULATIVE 누적 표시
    const rawRoundScores = (payload.roundScoresIfAny ?? payload.RoundScoresIfAny) as Array<Record<string, unknown>> | undefined
    if (rawRoundScores && rawRoundScores.length > 0) {
      // 최신 1라운드분만 lastRound로 추가 (정확한 구조는 서버 응답에 따라)
      void rawRoundScores
    }
  },
}
